<template>
  <div class="st-gare">
    <div class="ov-kpis">
      <div class="ov-kpi ov-kpi--primary">
        <div class="ov-kpi__label">Chiffre d'affaires</div>
        <div class="ov-kpi__value">{{ formatNumber(stats && stats.chiffreAffaires) }}<span>FCFA</span></div>
      </div>
      <div class="ov-kpi">
        <div class="ov-kpi__label">Total des réservations</div>
        <div class="ov-kpi__value">{{ formatNumber(stats && stats.nombreTotalReservationBilletVoyage) }}</div>
      </div>
      <div class="ov-kpi">
        <div class="ov-kpi__label">Chiffre d'affaires des bagages</div>
        <div class="ov-kpi__value">{{ formatNumber(luggageRevenue) }}<span>FCFA</span></div>
      </div>
    </div>

    <div class="ov-charts">
      <div class="ov-card">
        <div class="ov-card__head">
          <div>
            <div class="ov-card__title">Chiffre d'affaires par programme</div>
            <div class="ov-card__sub">{{ revenueChart.datasets.length > 1 ? 'Réservations et bagages, en FCFA' : 'Réservations, en FCFA' }}</div>
          </div>
        </div>
        <div class="ov-card__body">
          <Bar
            v-if="hasData(revenueChart)"
            :chart-id="chartId + '-revenue'"
            :chart-data="revenueChart"
            :chart-options="revenueOptions"
            :styles="chartStyles"
          />
          <div v-else class="ov-empty ov-chart-empty">Aucune donnée pour cette période.</div>
        </div>
      </div>

      <div class="ov-card">
        <div class="ov-card__head">
          <div>
            <div class="ov-card__title">Réservations par programme</div>
            <div class="ov-card__sub">Classement sur la période</div>
          </div>
        </div>
        <div class="ov-card__body">
          <Bar
            v-if="hasData(rankingChart)"
            :chart-id="chartId + '-ranking'"
            :chart-data="rankingChart"
            :chart-options="rankingOptions"
            :styles="rankingHeight(rankingChart)"
          />
          <div v-else class="ov-empty ov-chart-empty">Aucune réservation pour cette période.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar } from "vue-chartjs/legacy";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  LinearScale,
  CategoryScale,
} from "chart.js";
import {
  SERIES_RESERVATION,
  SERIES_LUGGAGE,
  CHART_STYLES,
  chartOptions,
  formatNumber,
  buildGroupedBars,
  buildRanking,
  mapFromChartData,
  hasData,
  rankingHeight,
} from "@/modules/statistiques/chartTheme";

ChartJS.register(Title, Tooltip, Legend, BarElement, LinearScale, CategoryScale);

// Premier objet { libellé: valeur } non vide parmi les clés proposées
const firstMap = (source, keys) => {
  if (!source) return null;
  const key = keys.find((k) => source[k] && Object.keys(source[k]).length);
  return key ? source[key] : null;
};

export default {
  name: "StatistiqueReservationParGareTransport",

  // Contrat inchangé : ce composant est utilisé par plusieurs tableaux de bord
  props: {
    dataStatistiqueGareTransport: {
      type: Object,
      default: null,
    },
    dataStatisticBagStation: {
      type: Object,
      default: null,
    },
    chartDataDoughnutGare: {
      type: Object,
      default: () => ({}),
    },
    chartDataGare: {
      type: Object,
      default: () => ({}),
    },
    chartId: {
      type: String,
      default: "gare-chart",
    },
    // Conservées pour compatibilité avec les parents (plus utilisées)
    datasetIdKey: { type: String, default: "label" },
    width: { type: Number, default: 400 },
    height: { type: Number, default: 400 },
    cssClasses: { type: String, default: "" },
    styles: { type: Object, default: () => ({}) },
    plugins: { type: Array, default: () => [] },
  },

  components: {
    Bar,
  },

  computed: {
    stats() {
      return this.dataStatistiqueGareTransport;
    },

    luggageRevenue() {
      if (this.dataStatisticBagStation && this.dataStatisticBagStation.chiffreAffaires != null) {
        return this.dataStatisticBagStation.chiffreAffaires;
      }
      return this.stats && this.stats.chiffreAffairesBagages;
    },

    // Les statistiques brutes sont la source de vérité ; les chartData préparés
    // par les parents ne servent que de secours (leurs séries bagages sont hétérogènes).
    revenueChart() {
      const reservations =
        firstMap(this.stats, ["chiffreAffairesParProgramme", "chiffreAffairesReservationParProgramme"]) ||
        mapFromChartData(this.chartDataGare, 0);
      const luggage = firstMap(this.stats, ["chiffreAffairesBagagesParProgramme", "chiffreAffairesBagageParProgramme"]);
      return buildGroupedBars([
        { label: "Chiffre d'affaires", map: reservations, color: SERIES_RESERVATION },
        { label: "Chiffre d'affaires des bagages", map: luggage, color: SERIES_LUGGAGE },
      ]);
    },

    rankingChart() {
      const counts =
        firstMap(this.stats, ["nombreReservationBilletVoyageParProgramme"]) ||
        mapFromChartData(this.chartDataDoughnutGare, 0);
      return buildRanking(counts, "Réservations");
    },

    revenueOptions() {
      return chartOptions({ currency: true, legend: this.revenueChart.datasets.length > 1 });
    },

    rankingOptions() {
      return chartOptions({ currency: false, legend: false, horizontal: true });
    },

    chartStyles() {
      return CHART_STYLES;
    },
  },

  methods: {
    formatNumber,
    hasData,
    rankingHeight,
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/admin/ui";

.st-gare {
  font-family: "Montserrat", sans-serif;
}
</style>
